import React, { useState, FC, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Avatar,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Markdown from 'react-markdown';
import { useLocation, useParams } from 'react-router-dom';

import { useGetResourceEventsQuery, useGetTrendsQuery } from './healthWalletSlice';
import { BRAND_DARK, BRAND_DARKER, AI_CHAT_DISCLAIMER } from './healthWalletConstants';
import { EventTimeline } from './EventTimeline';
import { useAuthProfile } from '../../app/hooks';

// --- Chat data link chip with chart badge ---
const ChatDataChip: FC<{
  resource: string;
  name: string;
  label: React.ReactNode;
  onClick: () => void;
}> = ({ resource, name, label, onClick }) => {
  // hasChart = the (resource, name) is a chartable series per get_patient_trends (shared cached fetch).
  const { data: trends } = useGetTrendsQuery();
  const hasChart = trends?.some((t) => t.resource === resource && t.name === name) ?? false;

  return (
    <Chip
      size="small"
      icon={<AssignmentIcon sx={{ fontSize: '14px !important' }} />}
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {hasChart && <ShowChartIcon sx={{ fontSize: 14, color: BRAND_DARKER, ml: 0.25 }} />}
        </Box>
      }
      onClick={onClick}
      sx={{ cursor: 'pointer', bgcolor: '#efeaff', color: BRAND_DARKER, fontWeight: 600, fontSize: '0.75rem', height: 24, mx: 0.25, '&:hover': { bgcolor: '#d7d3f0' } }}
    />
  );
};

// --- Embedded data viewer shown inside the chatbot when a healthwallet:// link is clicked ---
const ChatDataPanel: FC<{
  resource: string;
  name: string;
  onBack: () => void;
}> = ({ resource, name, onBack }) => {
  const { data: events, isLoading } = useGetResourceEventsQuery({ resource, name });
  const eventList = events ?? [];
  // Chart derived client-side from this group's numeric events (>=2 points sharing a unit) — same as
  // GroupedDetailDialog; the events endpoint no longer returns a precomputed chart.
  const chartData = (() => {
    const numeric = eventList
      .map((e: any, idx: number) => ({ e, idx }))
      .filter(({ e }) => e?.value !== null && e?.value !== undefined && !isNaN(Number(e.value)));
    if (numeric.length < 2) return null;
    const unit = numeric[0].e.unit ?? '';
    const pts = numeric.filter(({ e }) => (e.unit ?? '') === unit);
    if (pts.length < 2) return null;
    return { unit, data: pts.map(({ e, idx }) => ({ date: e.start ?? '', value: Number(e.value), eventIndex: idx })) };
  })();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #efeaff', bgcolor: 'white' }}>
        <IconButton size="small" onClick={onBack} aria-label="Back to chat" sx={{ color: BRAND_DARKER }}>
          <ChevronRightIcon sx={{ transform: 'rotate(180deg)' }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>{name || 'Data View'}</Typography>
          <Typography variant="caption" color="text.secondary">{resource} · {eventList.length} events</Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, bgcolor: '#f7f7fa' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: BRAND_DARKER }} /></Box>
        ) : (
          <EventTimeline
            eventList={eventList}
            chartData={chartData}
            resource={resource}
            groupName={name}
          />
        )}
      </Box>
    </Box>
  );
};

// --- Chatbot Component ---
interface ChatbotProps {
  externalOpen: boolean;
  onExternalClose: () => void;
  initialQuery: string;
  clearQuery: () => void;
  // Context hint for Polly (CEO ask 2026-07-12): which records tab the user is on — the renderer
  // passes its activeCategory; captured PER QUESTION at send time (mid-chat tab switches count).
  // Rides the query body → map relay → HW-AI (whose zod schema strips it until Prashanth accepts it).
  contextHint?: string;
}

export const Chatbot: FC<ChatbotProps> = ({ externalOpen, onExternalClose, initialQuery, clearQuery, contextHint }) => {
  const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile, } = useAuthProfile()
  const location = useLocation();
  const params = useParams();
  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [viewData, setViewData] = useState<{ resource: string; name: string } | null>(null);
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant', content: string }[]>([
    { role: 'assistant', content: AI_CHAT_DISCLAIMER },
    { role: 'assistant', content: "Hey there! I'm **Polly**, your health AI assistant. Ask me anything about your health data, conditions, medications — or just say hi. How can I help you today?" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingStatus, setThinkingStatus] = useState("Thinking...");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasSentInitial = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, thinkingStatus]);

  // Handle external open from top prompt
  useEffect(() => {
    if (externalOpen && initialQuery.trim() && !hasSentInitial.current) {
      setOpen(true);
      hasSentInitial.current = true;
      const q = initialQuery.trim();
      clearQuery();
      sendChatMessage(q);
    }
    if (!externalOpen) {
      hasSentInitial.current = false;
    }
  }, [externalOpen, initialQuery]);

  // Handle URL routing for healthwallet data links
  useEffect(() => {
    // Try to get route parameters first
    const { resource, name } = params;
    
    if (resource && name) {
      // If we have route parameters, use them directly
      setViewData({ resource, name });
      setOpen(true);
    } else {
      // Fallback to manual parsing for dynamic routes
      const pathname = location.pathname;
      if (pathname.startsWith('/healthwallet/') && pathname.length > '/healthwallet/'.length) {
        const pathParts = pathname.substring('/healthwallet/'.length).split('/');
        if (pathParts.length >= 2) {
          const resource = pathParts[0];
          const name = decodeURIComponent(pathParts.slice(1).join('/'));
          
          // Set viewData to show ChatDataPanel
          setViewData({ resource, name });
          setOpen(true);
        }
      }
    }
  }, [location.pathname, params]);

  const sendChatMessage = async (text: string) => {
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);
    setThinkingStatus("Thinking...");

    try {
      // const response = await fetch(
      //   '/api/healthwallet/patient_chat',
      //   {
      //     method: 'POST',
      //     headers: {
      //       'Content-Type': 'application/json',
      //       'id': loggedInUserProfile?.id?.toString() || '',
      //     },
      //     body: JSON.stringify({ prompt: text }),
      //   });


      // Route through the map server (not the AI service directly): the map resolves
      // patient_id from the authenticated profile server-side and relays the agent's
      // events (which the AI POSTs back to the map via callback) over this SSE. See
      // project_hw_multi_map_cross_access_plan.
      const response = await fetch(
         '/api/healthwallet/ai-query-stream',
         {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'id': loggedInUserProfile?.id?.toString() || '',
        },
        body: JSON.stringify({ query: text, ...(contextHint ? { contextHint } : {}) }),
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) throw new Error('No response stream');

      let buffer = '';
      let finalReply = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);
              if (eventType === 'status') {
                setThinkingStatus(data.message);
              } else if (eventType === 'done') {
                finalReply = data.reply;
              } else if (eventType === 'interrupt') {
                // Agent needs clarification — same `reply` payload as `done`; render it like
                // any assistant message. The user's next message answers it and resumes the
                // agent server-side (via the checkpointer); no extra client state needed.
                finalReply = data.reply;
              } else if (eventType === 'error') {
                throw new Error(data.error || 'Unknown error');
              }
            } catch (parseErr: any) {
              if (eventType === 'error') throw parseErr;
            }
            eventType = '';
          }
        }
      }

      if (finalReply) {
        setMessages(prev => [...prev, { role: 'assistant', content: finalReply }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: "Hmm, I didn't get a response. Could you try asking again?" }]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I ran into an issue processing your request. Let's try again?" }]);
    } finally {
      setIsLoading(false);
      setThinkingStatus("Thinking...");
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue("");
    await sendChatMessage(userMessage);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setOpen(false);
    onExternalClose();
  };

  return (
    <>
      <Fab
        aria-label="chat"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: { xs: 16, md: 32 }, right: { xs: 16, md: 32 },
          zIndex: 1300, boxShadow: '0 4px 20px rgba(108,76,224,0.3)',
          bgcolor: BRAND_DARKER, color: 'white',
          '&:hover': { bgcolor: '#5a3fc0' },
        }}
      >
        <ChatIcon />
      </Fab>

      <Dialog
        open={open} onClose={handleClose} maxWidth="sm" fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: maximized ? {
            position: 'fixed', inset: { xs: 0, sm: 16 },
            m: 0, width: 'auto', height: 'auto',
            maxWidth: '100%', maxHeight: '100%',
            borderRadius: { xs: 0, sm: 3 },
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            transition: 'all 0.2s ease',
          } : {
            position: { xs: 'fixed', sm: 'fixed' },
            bottom: { xs: 0, sm: 32 }, right: { xs: 0, sm: 32 },
            m: 0,
            width: { xs: '100%', sm: '420px' },
            height: { xs: '100%', sm: '620px' },
            maxHeight: { xs: '100%', sm: '620px' },
            borderRadius: { xs: 0, sm: 3 },
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            transition: 'all 0.2s ease',
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${BRAND_DARKER} 0%, ${BRAND_DARK} 100%)`,
          color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)' }} alt="">
              <AutoAwesomeIcon fontSize="small" aria-hidden="true" />
            </Avatar>
            <Typography variant="subtitle1" fontWeight="bold">Polly, the Health Assistant</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton onClick={() => setMaximized(!maximized)} aria-label={maximized ? 'Minimize chat' : 'Maximize chat'} sx={{ color: 'white', display: { xs: 'none', sm: 'flex' } }} size="small">
              {maximized ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
            <IconButton onClick={handleClose} aria-label="Close chat" sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {viewData ? (
          <ChatDataPanel resource={viewData.resource} name={viewData.name} onBack={() => setViewData(null)} />
        ) : (
          <>
            <DialogContent role="log" aria-live="polite" aria-label="Chat messages" sx={{ flexGrow: 1, p: 2, pt: '16px !important', bgcolor: '#f7f7fa', display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
              {messages.map((msg, idx) => (
                <Box
                  key={idx}
                  sx={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%', display: 'flex', flexDirection: 'column',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: msg.role === 'user' ? BRAND_DARKER : 'white',
                      color: msg.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2.5,
                      borderTopRightRadius: msg.role === 'user' ? 0 : 10,
                      borderTopLeftRadius: msg.role === 'assistant' ? 0 : 10,
                      border: msg.role === 'assistant' ? '1px solid #efeaff' : 'none',
                      '& p': { m: 0 }, '& ul, & ol': { my: 0.5, pl: 2.5 }, '& li': { mb: 0.25 },
                      '& code': { bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5, fontSize: '0.85em' },
                      '& a': { color: BRAND_DARKER, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <Typography variant="body2" component="div">
                        <Markdown urlTransform={(url) => url} components={{
                          a: ({ node, href, children, ...props }) => {
                            if (href?.startsWith('healthwallet://')) {
                              const parts = href.replace('healthwallet://', '').split('/');
                              const resource = parts[0];
                              const name = decodeURIComponent(parts.slice(1).join('/'));
                              return (
                                <ChatDataChip
                                  resource={resource}
                                  name={name}
                                  label={children}
                                  onClick={() => setViewData({ resource, name })}
                                />
                              );
                            }
                            return <a {...props} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
                          }
                        }}>{msg.content}</Markdown>
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                    )}
                  </Paper>
                </Box>
              ))}
              {isLoading && (
                <Box role="status" aria-live="assertive" sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                  <CircularProgress size={18} sx={{ color: BRAND_DARKER }} aria-hidden="true" />
                  <Typography variant="caption" color="text.secondary" sx={{
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } },
                  }}>{thinkingStatus}</Typography>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </DialogContent>

            <DialogActions sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid #efeaff' }}>
              <TextField
                fullWidth placeholder="Ask about your health data..."
                variant="outlined" size="small"
                aria-label="Chat message input"
                value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown} disabled={isLoading}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 50, bgcolor: '#f7f7fa', '&:hover fieldset': { borderColor: BRAND_DARK }, '&.Mui-focused fieldset': { borderColor: BRAND_DARKER } } }}
              />
              <IconButton
                onClick={handleSend}
                aria-label="Send message"
                disabled={!inputValue.trim() || isLoading}
                sx={{ bgcolor: BRAND_DARKER, color: 'white', '&:hover': { bgcolor: '#5a3fc0' }, '&.Mui-disabled': { bgcolor: '#e0e0e0' }, ml: 0.5, width: 38, height: 38 }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
